import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';

// Session timeout duration in milliseconds (1 hour)
const SESSION_TIMEOUT = 60 * 60 * 1000;

interface AuthContextType {
  currentUser: User | null;
  isAdmin: boolean;
  loading: boolean;
  adminLoading: boolean;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  checkAdminStatus: (user: User | null) => Promise<void>;
  resetSessionTimer: () => void;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  isAdmin: false,
  loading: true,
  adminLoading: false,
  logout: async () => {},
  resetPassword: async () => {},
  checkAdminStatus: async () => {},
  resetSessionTimer: () => {}
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminLoading, setAdminLoading] = useState(false);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());

  const checkAdminStatus = async (user: User | null) => {
    if (!user) {
      setIsAdmin(false);
      return;
    }

    try {
      setAdminLoading(true);
      const userRoleDoc = await getDoc(doc(db, 'userRoles', user.uid));
      if (userRoleDoc.exists()) {
        const userData = userRoleDoc.data();
        setIsAdmin(userData.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    } finally {
      setAdminLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setIsAdmin(false);
      setCurrentUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  // Reset the session timer
  const resetSessionTimer = useCallback(() => {
    setLastActivity(Date.now());
  }, []);

  // Check for session timeout
  useEffect(() => {
    if (!currentUser) return;

    const checkSessionTimeout = () => {
      const now = Date.now();
      if (now - lastActivity > SESSION_TIMEOUT) {
        console.log('Session timed out due to inactivity');
        logout();
      }
    };

    // Check every minute
    const interval = setInterval(checkSessionTimeout, 60000);

    // Add event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    const handleUserActivity = () => resetSessionTimer();

    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      clearInterval(interval);
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [currentUser, lastActivity, resetSessionTimer]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log('Auth state changed:', user ? 'User logged in' : 'No user');
      setCurrentUser(user);
      
      if (user) {
        setLastActivity(Date.now()); // Reset timer on login
        // Only check admin status if we're not in the signup flow
        if (!window.location.pathname.includes('/signup')) {
          await checkAdminStatus(user);
        }
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = {
    currentUser,
    isAdmin,
    loading,
    adminLoading,
    logout,
    resetPassword,
    checkAdminStatus,
    resetSessionTimer
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && !adminLoading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 